import { RefObject } from 'react';
export interface IData {
    [key: string]: any;
}

// 原有的 IRender 类型，保持向后兼容
export type IRender = (td: IData, rowIndex?: number, index?: number) => JSX.Element | number | string;

// 泛型版本的 IRender，支持自定义数据类型
export type IRenderGeneric<T = IData> = (td: T, rowIndex?: number, index?: number) => JSX.Element | number | string;

export enum TextAlign {
    revert = 'revert',
    unset = 'unset',
    left = 'left',
    center = 'center',
    end = 'end',
    justify = 'justify',
    right = 'right',
}

export enum FixedDirection {
    left = 'left',
    right = 'right',
    auto = '',
}

export interface IStyle {
    width?: string;
    textAlign?: TextAlign;
    left?: string;
    right?: string;
}

export interface ICalculatedColumn<T = IData> extends IColumn<T> {
    position?: number;
}

export enum ScrollProcess {
    start = 'start',
    process = 'process',
    end = 'end',
}

export interface IColumn<T = IData> {
    key?: string;
    dataIndex: string; // data 索引，支持 'a.b.c' 的形式支持多层级数据
    name: string | JSX.Element; // th
    title?: string; // 鼠标 hover title，未设置则取 name
    width?: number | string; // 100px、10%、'auto'
    fixed?: FixedDirection; // 当前列是否固定悬浮, 须配合 width 使用(必须为具体值)
    textAlign?: TextAlign;
    className?: string;
    render?: IRenderGeneric<T>; // 自定义渲染 td，支持泛型
    children?: IColumn<T>[];
}

export interface TheadRowItem<T = IData> {
    column: IColumn<T>;
    colSpan?: number;
    rowSpan?: number;
    hasSubColumns?: boolean;
    colStart?: number;
    colEnd?: number;
    childrenCount?: number;
}

/**
 * 可以扩展react-virtualized的参数
 * 目前的以使用为主，原则是：保持简单，用到再加，不做未来设计。
 */
export interface IVirtualizedListProps {
    /**
     * Width of Grid; this property determines the number of visible (vs virtualized) columns.
     */
    width: number;
    height: number;
}

export interface IProps<T = IData> {
    className?: string;
    width?: number; // table 宽度，默认 100%，超过容器自动滚动
    data: T[];
    isMultilevel?: boolean;
    refTable?: RefObject<HTMLDivElement>;
    scrollNumber?: number;
    columns: IColumn<T>[];
    miniNumTdWidth?: number;
    onColumnClick?: (item: T, dataIndex?: string) => void;
    blankListText?: string;
    blank?: string | JSX.Element; // 为空时的占位
    children?: string | JSX.Element; // 为空时的占位
    rowSelection?: IRowSelection<T>;
    isTransformPx2Rem?: boolean;
    isVirtualized?: boolean; // 是否使用虚拟列表形式
    virtualizedListProps?: IVirtualizedListProps;
}

export enum ISelectMethods {
    ALL = 'all',
    MULTIPLE = 'multiple',
}

export interface ISelectionInfo {
    type: ISelectMethods;
}

export interface IRowSelection<T = IData> {
    columnWidth?: string | number;
    columnTitle?: string | React.ReactNode | (() => React.ReactNode);
    customizeRenderCell?: (record: T, _rowIndex?: number, _index?: number) => JSX.Element;
    onSelectAll?: (selected: boolean, allKeys: string[], changeKeys: string[], changeRows: T[]) => void;
    onSelectionChange?: (selectedRowKeys: string[], selectedRows: T[], info: ISelectionInfo) => void;
    preserveSelectedRowKeys?: string[];
    key: string;
}

export interface IConfig<T = IData> {
    columns: IColumn<T>[];
    data: T[];
}

export type FlatColumnsFirstLevel<T = IData> = { [key: string]: IColumn<T>[] };
